import { getStoreAccessors } from 'typesafe-vuex';
import { Store } from 'vuex';
import { State } from '../state';
import { MainState } from './state';
import { Locale } from '@/interfaces/api/general/language';
import { SdsMaximumUpload } from '@/lib/enum/SdsMaximumUpload';

export const getters = {
  dashboardShowDrawer: (state: MainState) => state.dashboardShowDrawer,
  dashboardMiniDrawer: (state: MainState) => state.dashboardMiniDrawer,
  userProfile: (state: MainState) => state.userProfile,
  isLoggedIn: (state: MainState) => Boolean(state.userProfile),
  company: (state: MainState) => state.company,
  companyId: (state: MainState) => {
    if (!state.company) {
      return null;
    }
    const companyId = state.company.uuid;
    return companyId;
  },
  isCompanySubscription: (state: MainState): boolean => {
    return state.company
      ? state.company.maximum_upload > SdsMaximumUpload.DEFAULT.value
      : false;
  },
  switchableCompanies: (state: MainState) => state.switchableCompanies,
  hasSwitchableCompanies: (state: MainState): boolean => {
    return state.switchableCompanies.length > 1;
  },
  departments: (state: MainState) => state.departments,
  firstNotification: (state: MainState) =>
    state.notifications.length > 0 && state.notifications[0],
  userLocale: (state: MainState): Locale =>
    state.userProfile
      ? state.userProfile?.locale || state.nonLoggedInUserLocale
      : state.nonLoggedInUserLocale,
  initialized: (state: MainState) => state.initialized,
  forceEnroll2Fa: (state: MainState) => state.forceEnroll2Fa,
  isFeatureEnabled:
    (state: MainState) =>
    (featureName: string): boolean => {
      return state.featureFlags[featureName] || false;
    },
  getPollingInterval: (state: MainState) => state.pollingIntervalOfFeatureFlags,
};

const { read } = getStoreAccessors<MainState, State>('');

export const readDashboardMiniDrawer = read(getters.dashboardMiniDrawer);
export const readDashboardShowDrawer = read(getters.dashboardShowDrawer);
export const readCompany = read(getters.company);
export const readCompanyId = read(getters.companyId);
export const readSwitchableCompanies = read(getters.switchableCompanies);
export const readHasSwitchableCompanies = read(getters.hasSwitchableCompanies);
export const readDepartments = read(getters.departments);
export const readUserProfile = read(getters.userProfile);
export const readFirstNotification = read(getters.firstNotification);
export const readIsCompanySubscription = read(getters.isCompanySubscription);
export const readUserLocale = read(getters.userLocale);
export const readInitialized = read(getters.initialized);
export const readForceEnroll2Fa = read(getters.forceEnroll2Fa);
export const readIsLoggedIn = read(getters.isLoggedIn);
export const readIsFeatureEnabled = read(getters.isFeatureEnabled);
export const readGetPollingInterval = read(getters.getPollingInterval);

// Asserted readers to be used to avoid repetitive type guarding
// in places where the value is always expected to be present.
export const readAssertedUserProfile = (store: Store<State>) => {
  const userProfile = readUserProfile(store);
  if (!userProfile) {
    throw new Error(
      'User profile was expected to be present in global store but was not'
    );
  }
  return userProfile;
};
export const readAssertedCompany = (store: Store<State>) => {
  const company = readCompany(store);
  if (!company) {
    throw new Error(
      'Company was expected to be present in global store but was not'
    );
  }
  return company;
};
export const readAssertedCompanyId = (store: Store<State>) => {
  const companyId = readCompanyId(store);
  if (!companyId) {
    throw new Error(
      'Company ID was expected to be present in global store but was not'
    );
  }
  return companyId;
};
export const readAssertedDepartments = (store: Store<State>) => {
  const departments = readDepartments(store);
  if (!departments) {
    throw new Error(
      'Departments were expected to be present in global store but were not'
    );
  }
  return departments;
};
